import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import { legalEntityKeys } from './useLegalEntities';
import { supplierLedgerKeys } from './useSupplierLedger';

export const accountingAccountKeys = {
  all: ['accountingAccounts'],
  lists: () => [...accountingAccountKeys.all, 'list'],
  list: (filters = {}) => [...accountingAccountKeys.lists(), filters],
  details: () => [...accountingAccountKeys.all, 'detail'],
  detail: (id) => [...accountingAccountKeys.details(), id],
};

const normalizePayload = (data = {}) => {
  const payload = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return;
    if (typeof value === 'string') {
      // Un string vacio se manda como null para poder limpiar codigo, descripcion o concepto.
      const trimmed = value.trim();
      payload[key] = trimmed || null;
      return;
    }
    payload[key] = value;
  });

  if (payload.default_cost_traceability && !payload.default_cost_nature) {
    payload.default_cost_nature = payload.default_cost_traceability;
  }

  delete payload.default_cost_traceability;
  return payload;
};

export const useAccountingAccounts = (filters = {}, options = {}) =>
  useQuery({
    queryKey: accountingAccountKeys.list(filters),
    queryFn: async () => {
      const response = await apiClient.get('accounting_accounts', { params: filters });
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    ...options,
  });

export const useCreateAccountingAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await apiClient.post('accounting_accounts', normalizePayload(data));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingAccountKeys.all });
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.lists() });
    },
  });
};

export const useUpdateAccountingAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`accounting_accounts/${id}`, normalizePayload(data));
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(accountingAccountKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: accountingAccountKeys.all });
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.lists() });
    },
  });
};

export const useDeleteAccountingAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`accounting_accounts/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingAccountKeys.all });
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.lists() });
    },
  });
};
